import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, ChevronDown, ChevronUp, Home, Camera, Users, GripVertical, Eye, EyeOff } from 'lucide-react';

const iconMap = {
  home: Home,
  camera: Camera,
  users: Users
};

const AdminCalculator = () => {
  const [config, setConfig] = useState({ services: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedService, setExpandedService] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/calculator/config', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setConfig(data);
      if (data.services?.length > 0) {
        setExpandedService(data.services[0].id);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/calculator/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Конфигурация сохранена!' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка сохранения' });
    } finally {
      setSaving(false);
    }
  };

  const updateService = (serviceId, field, value) => {
    setConfig(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.id === serviceId ? { ...s, [field]: value } : s
      )
    }));
  };

  const updatePackage = (serviceId, packageId, field, value) => {
    setConfig(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.id === serviceId
          ? {
              ...s,
              packages: s.packages.map(p =>
                p.id === packageId ? { ...p, [field]: value } : p
              )
            }
          : s
      )
    }));
  };

  const updateExtra = (serviceId, extraId, field, value) => {
    setConfig(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.id === serviceId
          ? {
              ...s,
              extras: s.extras.map(e =>
                e.id === extraId ? { ...e, [field]: value } : e
              )
            }
          : s
      )
    }));
  };

  const addPackage = (serviceId) => {
    const newPackage = {
      id: `pkg-${Date.now()}`,
      price: 100,
      photos: 10,
      label_de: 'Neues Paket',
      label_ru: 'Новый пакет'
    };
    setConfig(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.id === serviceId
          ? { ...s, packages: [...s.packages, newPackage] }
          : s
      )
    }));
  };

  const removePackage = (serviceId, packageId) => {
    setConfig(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.id === serviceId
          ? { ...s, packages: s.packages.filter(p => p.id !== packageId) }
          : s
      )
    }));
  };

  const addExtra = (serviceId) => {
    const newExtra = {
      id: `extra-${Date.now()}`,
      price: 50,
      label_de: 'Neue Option',
      label_ru: 'Новая опция',
      active: true
    };
    setConfig(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.id === serviceId
          ? { ...s, extras: [...s.extras, newExtra] }
          : s
      )
    }));
  };

  const removeExtra = (serviceId, extraId) => {
    setConfig(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.id === serviceId
          ? { ...s, extras: s.extras.filter(e => e.id !== extraId) }
          : s
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-white">Калькулятор цен</h1>
          <p className="text-gray-400 mt-1">
            Управление ценами и пакетами для калькулятора
          </p>
        </div>
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
        >
          <Save className="h-5 w-5" />
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}`}>
          {message.text}
        </div>
      )}

      {/* Services */}
      <div className="space-y-4">
        {config.services?.map((service) => {
          const IconComponent = iconMap[service.icon] || Home;
          const isExpanded = expandedService === service.id;

          return (
            <div key={service.id} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              {/* Service Header */}
              <div
                onClick={() => setExpandedService(isExpanded ? null : service.id)}
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-700/50"
              >
                <div className="flex items-center gap-3">
                  <IconComponent className="h-6 w-6 text-indigo-400" />
                  <div>
                    <h3 className="font-semibold text-white">{service.name_de}</h3>
                    <p className="text-sm text-gray-400">{service.name_ru}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateService(service.id, 'active', !service.active);
                    }}
                    className={`p-2 rounded ${service.active ? 'text-green-400' : 'text-gray-500'}`}
                  >
                    {service.active ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                  </button>
                  {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-gray-700 p-4 space-y-6">
                  {/* Per Photo Price */}
                  <div className="flex items-center gap-4">
                    <label className="text-sm text-gray-400 w-48">Цена за доп. фото:</label>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">€</span>
                      <input
                        type="number"
                        value={service.per_photo_price}
                        onChange={(e) => updateService(service.id, 'per_photo_price', parseInt(e.target.value) || 0)}
                        className="w-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                      />
                    </div>
                  </div>

                  {/* Packages */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-white">Пакеты</h4>
                      <button
                        onClick={() => addPackage(service.id)}
                        className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                      >
                        <Plus className="h-4 w-4" /> Добавить
                      </button>
                    </div>
                    <div className="space-y-3">
                      {service.packages?.map((pkg) => (
                        <div key={pkg.id} className="bg-gray-900/50 rounded-lg p-4 grid grid-cols-12 gap-3 items-center">
                          <div className="col-span-3">
                            <label className="text-xs text-gray-500 block mb-1">Название (DE)</label>
                            <input
                              type="text"
                              value={pkg.label_de}
                              onChange={(e) => updatePackage(service.id, pkg.id, 'label_de', e.target.value)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                            />
                          </div>
                          <div className="col-span-3">
                            <label className="text-xs text-gray-500 block mb-1">Название (RU)</label>
                            <input
                              type="text"
                              value={pkg.label_ru}
                              onChange={(e) => updatePackage(service.id, pkg.id, 'label_ru', e.target.value)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs text-gray-500 block mb-1">Цена (€)</label>
                            <input
                              type="number"
                              value={pkg.price}
                              onChange={(e) => updatePackage(service.id, pkg.id, 'price', parseInt(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs text-gray-500 block mb-1">Фото вкл.</label>
                            <input
                              type="number"
                              value={pkg.photos}
                              onChange={(e) => updatePackage(service.id, pkg.id, 'photos', parseInt(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2 flex justify-end">
                            <button
                              onClick={() => removePackage(service.id, pkg.id)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Extras */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-white">Дополнительные опции</h4>
                      <button
                        onClick={() => addExtra(service.id)}
                        className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                      >
                        <Plus className="h-4 w-4" /> Добавить
                      </button>
                    </div>
                    <div className="space-y-3">
                      {service.extras?.map((extra) => (
                        <div key={extra.id} className="bg-gray-900/50 rounded-lg p-4 grid grid-cols-12 gap-3 items-center">
                          <div className="col-span-3">
                            <label className="text-xs text-gray-500 block mb-1">Название (DE)</label>
                            <input
                              type="text"
                              value={extra.label_de}
                              onChange={(e) => updateExtra(service.id, extra.id, 'label_de', e.target.value)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                            />
                          </div>
                          <div className="col-span-3">
                            <label className="text-xs text-gray-500 block mb-1">Название (RU)</label>
                            <input
                              type="text"
                              value={extra.label_ru}
                              onChange={(e) => updateExtra(service.id, extra.id, 'label_ru', e.target.value)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs text-gray-500 block mb-1">Цена (€)</label>
                            <input
                              type="number"
                              value={extra.price}
                              onChange={(e) => updateExtra(service.id, extra.id, 'price', parseInt(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <button
                              onClick={() => updateExtra(service.id, extra.id, 'active', !extra.active)}
                              className={`px-3 py-1.5 rounded text-sm ${extra.active ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                            >
                              {extra.active ? 'Активно' : 'Скрыто'}
                            </button>
                          </div>
                          <div className="col-span-2 flex justify-end">
                            <button
                              onClick={() => removeExtra(service.id, extra.id)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info */}
      <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
        <p className="text-blue-300 text-sm">
          💡 Изменения автоматически синхронизируются на странице калькулятора и в мини-калькуляторе на странице бронирования.
        </p>
      </div>
    </div>
  );
};

export default AdminCalculator;
